// src/components/player/tiles/Tile1Performance30d.js
// Tile 1: 30-Day Performance Benchmarking
// Displays player's 30-day performance with MLB and league benchmarks

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { TileHitterGrid } from '../../../services/tableService';
import { TrendingUp } from 'lucide-react';

const Tile1Performance30d = ({ data, loading, error }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Check data structure
  const hasPlayerData = data?.player;
  const hasPlayerStats = data?.player?.stats;
  const hasLeagueBenchmark = data?.league_benchmark;

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <TrendingUp className="w-4 h-4 mr-2" />
          30-Day Performance
        </h4>
        
        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Check console for details
            </div>
          </div>
        ) : hasPlayerData ? (
          <div className="flex-1 flex flex-col">
            {/* If we have the right structure for TileHitterGrid */}
            {hasPlayerStats ? (
              <div className="flex-1">
                <TileHitterGrid 
                  data={data}
                  showLeague={hasLeagueBenchmark}
                />
              </div>
            ) : (
              // Fallback display if structure is different
              <div className="flex-1 space-y-2">
                {Object.entries(data.player || {}).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className={`text-xs ${dynastyTheme.classes.text.neutral}`}>{key}</span>
                    <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
                      {typeof value === 'number' ? value.toFixed(2) : value}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Footer with sample size and period info */}
            {data.player && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div className="flex justify-between">
                  <span>
                    {data.player.period_label || 
                     (data.player.period_days 
                       ? `Last ${data.player.period_days} days` 
                       : 'Recent performance')}
                  </span>
                  <span>{data.player.games} games</span>
                </div>
                {data.mlb_benchmark?.sample_size > 0 && (
                  <div className="text-xs opacity-60 mt-1">
                    MLB: {data.mlb_benchmark.sample_size} hitters
                  </div>
                )}
                {hasLeagueBenchmark && data.league_benchmark?.sample_size > 0 && (
                  <div className="text-xs opacity-60">
                    League: {data.league_benchmark.sample_size} hitters
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // No valid data structure
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              No performance data available
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile1Performance30d;
